import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import JobManager from "../components/JobManager";
import ContestacaoInputDisplay from "../components/ContestacaoInputDisplay";
import ContestacaoOutputDisplay from "../components/ContestacaoOutputDisplay";
import CellParamsModal from "../components/CellParamsModal";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { Settings } from "@material-ui/icons";

const ContestacaoPage = () => {
  const history = useHistory();
  const { t } = useTranslation('jobs');
  const { cell, user } = useAuth();
  
  // Estado para controlar o modal de parâmetros da célula
  const [cellParamsModalOpen, setCellParamsModalOpen] = useState(false);

  // Configuração das colunas da tabela específicas para contestação
  const columns = [
    {
      key: "insertion_timestamp",
      label: t('contestacao.columns.dateTime'),
      type: "date",
      sortable: true,
    },
    {
      key: "cell_name",
      label: t('contestacao.columns.cell'),
      type: "cell",
      sortable: true,
    },
    {
      key: "status",
      label: t('contestacao.columns.status'),
      type: "status",
      sortable: true,
      formatValue: (value) => {
        return t(`status.${value}`) || value;
      },
    },
    {
      key: "user_name",
      label: t('contestacao.columns.user'),
      type: "user",
      sortable: true,
    },
    // Nova coluna TOTAL DE PÁGINAS antes de Dados de Entrada
    {
      key: "total_pages",
      label: t('contestacao.columns.totalPages'),
      type: "custom",
      sortable: false,
      renderCell: (job) => (job?.input_payload?.total_paginas ?? "-")
    },
    {
      key: "input_payload",
      label: t('contestacao.columns.inputData'),
      type: "custom",
      sortable: false,
      renderCell: (job) => (
        <ContestacaoInputDisplay inputPayload={job.input_payload} />
      ),
    },
    {
      key: "output_payload",
      label: t('contestacao.columns.generatedDocument'),
      type: "custom",
      sortable: false,
      renderCell: (job) => (
        <ContestacaoOutputDisplay
          outputPayload={job.output_payload}
          status={job.status}
        />
      ),
    },
    // {
    //   key: "actions",
    //   label: t('table.actions'),
    //   type: "actions",
    //   sortable: false,
    // },
  ];

  // Configuração dos campos de filtro específicos para contestação
  const filterFields = [
    // Filtros obrigatórios para todos os tipos de trabalho
    {
      key: "job_id",
      label: t('filters.jobId'),
      type: "text",
      placeholder: t('filters.jobIdPlaceholder'),
    },
    {
      key: "external_id",
      label: t('filters.externalId'),
      type: "text",
      placeholder: t('filters.externalIdPlaceholder'),
    },
    {
      key: "date_start",
      label: t('filters.dateStart'),
      type: "date",
    },
    {
      key: "date_end",
      label: t('filters.dateEnd'),
      type: "date",
    },
    // Filtros específicos para contestação
    {
      key: "user_name_filter",
      label: t('filters.user'),
      type: "text",
      placeholder: t('filters.userPlaceholder'),
    },
    {
      key: "status_filter",
      label: t('filters.status'),
      type: "select",
      options: [
        { value: "DONE", label: t('status.DONE') },
        { value: "WAITING", label: t('status.WAITING') },
        { value: "RUNNING", label: t('status.RUNNING') },
        { value: "ERROR", label: t('status.ERROR') },
        { value: "FINAL_ERROR", label: t('status.FINAL_ERROR') },
      ],
    },
    {
      key: "output_payload_text_filter",
      label: t('contestacao.filters.documentContent'),
      type: "text",
      placeholder: t('contestacao.filters.documentContentPlaceholder'),
    },
  ];

  // Ações customizadas específicas para contestação (comentado temporariamente)
  // const customActions = [
  //   {
  //     key: "review",
  //     icon: <AssignmentTurnedIn style={{ fontSize: "1rem" }} />,
  //     tooltip: t('contestacao.actions.review'),
  //     disabled: (job) => job.status !== "DONE",
  //   },
  //   {
  //     key: "regenerate",
  //     icon: <Gavel style={{ fontSize: "1rem" }} />,
  //     tooltip: t('contestacao.actions.regenerate'),
  //     disabled: (job) => job.status === "PROCESSING",
  //   },
  // ];

  // Handlers para ações
  const handleNewJob = () => {
    history.push("/contestacao/nova");
  };

  const handleJobClick = (job) => {
    console.log("Contestação clicada:", job);
    // Navegar para detalhes da contestação
    history.push(`/contestacao/detalhes/${job.id}`);
  };

  const handleJobAction = (action, job) => {
    console.log("Ação executada:", action, "na contestação:", job);

    switch (action) {
      case "view":
        // Visualizar detalhes da contestação
        console.log("Visualizar contestação:", job.id);
        history.push(`/contestacao/detalhes/${job.id}`);
        break;

      case "download":
        // Fazer download do documento gerado
        console.log("Download documento:", job.id);
        downloadContestacaoDocument(job);
        break;

      case "review":
        // Abrir ferramenta de revisão
        console.log("Revisar contestação:", job.id);
        history.push(`/contestacao/revisar/${job.id}`);
        break;

      case "regenerate":
        // Regenerar o documento
        console.log("Regenerar documento:", job.id);
        regenerateDocument(job);
        break;

      default:
        console.log("Ação desconhecida:", action);
    }
  };

  const downloadContestacaoDocument = (job) => {
    // Implementar download do documento da contestação
    if (job.output_payload) {
      try {
        // Assumindo que o output_payload contém um documento em formato específico
        const blob = new Blob([job.output_payload], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `contestacao_${job.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao fazer download:", error);
      }
    }
  };

  const regenerateDocument = (job) => {
    // Implementar regeneração do documento
    console.log("Regenerando documento para contestação:", job.id);
    // Aqui você faria uma chamada para a API para regenerar o documento
  };

  // Função para verificar se o usuário tem permissão para configurar parâmetros da célula
  const hasContestParamsPermission = () => {
    return user && user.permissions && (
      user.permissions.includes('CONTEST_PARAMS') ||
      user.permissions.includes('CONTEST_PARAMS_SUPERUSER')
    );
  };

  // Função para abrir o modal de configuração de parâmetros
  const handleOpenCellParams = () => {
    setCellParamsModalOpen(true);
  };

  // Função para fechar o modal de configuração de parâmetros
  const handleCloseCellParams = () => {
    setCellParamsModalOpen(false);
  };

  // Criar ações customizadas do header específicas para contestação
  const customHeaderActions = hasContestParamsPermission() ? [
    {
      icon: <Settings />,
      onClick: handleOpenCellParams,
      disabled: !cell?.value, // Desabilita se não há célula selecionada
      style: {
        color: 'var(--ikaro-orange-primary)',
      }
    }
  ] : [];

  return (
    <>
      <JobManager
        jobType="contestacao"
        title={t('contestacao.title')}
        subtitle={t('contestacao.subtitle')}
        apiEndpoint="/job/get_jobs_list"
        createJobPath="/contestacao/nova"
        columns={columns}
        filterFields={filterFields}
        // customActions={customActions}
        customHeaderActions={customHeaderActions}
        onNewJob={handleNewJob}
        onJobClick={handleJobClick}
        onJobAction={handleJobAction}
        customFilters={{
          code: "DOCUMENT_CREATION_CONTESTATION", // Código específico para criação de contestação jurídica
        }}
        newJobButtonLabel={t('contestacao.newContestation')}
        filterButtonLabel={t('header.filters')}
      />

      {/* Modal de configuração de parâmetros da célula */}
      <CellParamsModal
        open={cellParamsModalOpen}
        onClose={handleCloseCellParams}
        cellId={cell?.value}
      />
    </>
  );
};

export default ContestacaoPage;
